// =====================================================
// PAGE ADMIN: VÉRIFICATION KYC PRATICIEN
// =====================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { FileText, CheckCircle, XCircle, AlertCircle, Clock, ArrowLeft } from 'lucide-react';
import { getPractitionerById, updatePractitionerStatus } from '../../../services/psyPractitionerService';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

export default function AdminPractitionerVerification() {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (id) {
      loadPractitioner(id);
    }
  }, [id]);

  const loadPractitioner = async (practitionerId: string) => {
    try {
      setLoading(true);
      const data = await getPractitionerById(practitionerId);
      setPractitioner(data);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Praticien non trouvé',
        variant: 'destructive',
      });
      navigate('/admin/psy/praticiens');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!practitioner) return;

    if (window.confirm('Approuver ce praticien ?')) {
      try {
        setProcessing(true);
        await updatePractitionerStatus(practitioner.id, 'approved');
        toast({
          title: 'Succès',
          description: 'Praticien approuvé',
        });
        navigate('/admin/psy/praticiens');
      } catch (error) {
        console.error('Erreur:', error);
        toast({
          title: 'Erreur',
          description: 'Erreur lors de l\'approbation',
          variant: 'destructive',
        });
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleReject = async () => {
    if (!practitioner || !rejectionReason) {
      toast({
        title: 'Erreur',
        description: 'Veuillez indiquer une raison de rejet',
        variant: 'destructive',
      });
      return;
    }

    if (window.confirm('Rejeter ce praticien ?')) {
      try {
        setProcessing(true);
        await updatePractitionerStatus(practitioner.id, 'rejected', rejectionReason);
        toast({
          title: 'Succès',
          description: 'Praticien rejeté',
        });
        navigate('/admin/psy/praticiens');
      } catch (error) {
        console.error('Erreur:', error);
        toast({
          title: 'Erreur',
          description: 'Erreur lors du rejet',
          variant: 'destructive',
        });
      } finally {
        setProcessing(false);
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center p-12">
          <Clock className="mr-2 h-8 w-8 animate-spin text-yellow-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!practitioner) return null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/psy/praticiens')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Vérification KYC</h1>
            <p className="text-gray-400">Examinez les documents et qualifications</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Informations personnelles</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400 mb-1">Nom complet</div>
                  <div className="font-medium text-white">{practitioner.full_name}</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Email</div>
                  <div className="font-medium text-white">{practitioner.email}</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Téléphone</div>
                  <div className="font-medium text-white">{practitioner.phone}</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Ville</div>
                  <div className="font-medium text-white">{practitioner.city}</div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Qualifications</h2>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-gray-400 mb-1">Numéro de licence</div>
                  <div className="font-medium text-white">{practitioner.license_number}</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Formation</div>
                  <div className="font-medium text-white whitespace-pre-line">
                    {practitioner.education}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Années d'expérience</div>
                  <div className="font-medium text-white">
                    {practitioner.years_of_experience} ans
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Spécialités</div>
                  <div className="flex flex-wrap gap-2">
                    {practitioner.specialties?.map((specialty: string) => (
                      <span
                        key={specialty}
                        className="px-3 py-1 bg-blue-400/20 text-blue-400 border border-blue-400/20 rounded-full text-xs font-medium"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Documents KYC</h2>
              <div className="space-y-3">
                {practitioner.kyc_documents?.license && (
                  <div className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-white">Licence professionnelle</span>
                    </div>
                    <button className="text-yellow-400 hover:text-yellow-300 text-sm font-medium">
                      Voir
                    </button>
                  </div>
                )}
                {practitioner.kyc_documents?.diploma && (
                  <div className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-white">Diplôme</span>
                    </div>
                    <button className="text-yellow-400 hover:text-yellow-300 text-sm font-medium">
                      Voir
                    </button>
                  </div>
                )}
                {practitioner.kyc_documents?.id_card && (
                  <div className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-white">Pièce d'identité</span>
                    </div>
                    <button className="text-yellow-400 hover:text-yellow-300 text-sm font-medium">
                      Voir
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sticky top-8">
              <h3 className="font-bold text-white mb-4">Décision</h3>

              {practitioner.verification_status === 'pending' ? (
                <div className="space-y-4">
                  <Button
                    onClick={handleApprove}
                    disabled={processing}
                    className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Approuver
                  </Button>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Raison du rejet
                    </label>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      placeholder="Expliquez pourquoi..."
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-400 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <Button
                    onClick={handleReject}
                    disabled={processing || !rejectionReason}
                    className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                  >
                    <XCircle className="w-5 h-5" />
                    Rejeter
                  </Button>
                </div>
              ) : (
                <div
                  className={`p-4 rounded-lg flex items-center gap-3 ${
                    practitioner.verification_status === 'approved'
                      ? 'bg-green-400/20 text-green-400 border border-green-400/20'
                      : 'bg-red-400/20 text-red-400 border border-red-400/20'
                  }`}
                >
                  {practitioner.verification_status === 'approved' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <div>
                    <div className="font-semibold">
                      {practitioner.verification_status === 'approved' ? 'Approuvé' : 'Rejeté'}
                    </div>
                    {practitioner.rejection_reason && (
                      <div className="text-sm mt-1">{practitioner.rejection_reason}</div>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="text-sm text-gray-400 mb-2">Informations</div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-400">Créé le:</span>{' '}
                    <span className="font-medium text-white">
                      {new Date(practitioner.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Tarif:</span>{' '}
                    <span className="font-medium text-white">
                      {practitioner.base_rate_per_session.toLocaleString()} FCFA
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
